import { useState, useEffect } from "react";
import {
  Activity,
  BarChart3,
  ExternalLink,
  FileText,
  Home,
  Image,
  LayoutDashboard,
  LogOut,
  Mail,
  Megaphone,
  Menu,
  MessageSquare,
  Radio,
  Search,
  Server,
  Settings,
  Shield,
  Tag,
  Users,
  X,
  ChevronRight
} from "lucide-react";
import { Link, NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../services/auth";
import { LoadingState } from "../components/LoadingState";

type NavItem = {
  to: string;
  label: string;
  icon: any;
  end?: boolean;
  permission?: string;
  badge?: string;
};

type NavSection = {
  heading: string;
  items: NavItem[];
};

const navSections: NavSection[] = [
  {
    heading: "EDITORIAL DESK",
    items: [
      { to: "/admin", label: "Operations Dashboard", icon: LayoutDashboard, end: true },
      { to: "/admin/articles", label: "Articles & Drafts", icon: FileText, permission: "articles.view" },
      { to: "/admin/homepage", label: "Front Page Curation", icon: Home, permission: "homepage.manage" },
      { to: "/admin/media", label: "Media Asset Vault", icon: Image, permission: "media.view" },
      { to: "/admin/taxonomy", label: "Taxonomies & Tags", icon: Tag, permission: "categories.manage" },
      { to: "/admin/search", label: "Archive Index & Search", icon: Search, permission: "articles.view" }
    ]
  },
  {
    heading: "COMMUNITY & REVENUE",
    items: [
      { to: "/admin/advertising", label: "Ad Placements & Sponsors", icon: Megaphone, permission: "advertising.manage" },
      { to: "/admin/comments", label: "Reader Discussions", icon: MessageSquare, permission: "comments.manage" },
      { to: "/admin/newsletter", label: "Subscribers & Broadcasts", icon: Mail, permission: "newsletter.manage" },
      { to: "/admin/analytics", label: "Audience Telemetry", icon: BarChart3, permission: "analytics.view" }
    ]
  },
  {
    heading: "GOVERNANCE & SYSTEM",
    items: [
      { to: "/admin/users", label: "Staff, Authors & Roles", icon: Users, permission: "users.manage" },
      { to: "/admin/system", label: "System Health & Engine", icon: Activity, permission: "settings.manage" },
      { to: "/admin/settings", label: "Platform Settings", icon: Settings, permission: "settings.manage" }
    ]
  }
];

export function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Close mobile drawer when route changes
  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileDrawerOpen) {
      document.body.classList.add("admin-drawer-locked");
    } else {
      document.body.classList.remove("admin-drawer-locked");
    }
    return () => document.body.classList.remove("admin-drawer-locked");
  }, [mobileDrawerOpen]);

  if (loading) return <LoadingState label="Verifying newsroom access..." />;
  if (!user) return <Navigate to="/admin/login" replace />;

  const isSuperAdmin =
    user.permissions.includes("*") ||
    user.roles.some((r) => ["Super Administrator", "Administrator", "admin"].includes(r));

  // Filter sections and items based on permissions
  const visibleSections = navSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (!item.permission) return true;
        if (isSuperAdmin) return true;
        return user.permissions.includes(item.permission);
      })
    }))
    .filter((section) => section.items.length > 0);

  return (
    <div className="admin-shell">
      {/* Mobile Backdrop Overlay */}
      {mobileDrawerOpen && (
        <div
          className="admin-mobile-backdrop"
          onClick={() => setMobileDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Slide-in Drawer Sidebar */}
      <aside className={`admin-sidebar ${mobileDrawerOpen ? "is-mobile-open" : ""}`}>
        <div className="admin-sidebar-header">
          <Link to="/admin" className="admin-brand">
            <span className="admin-brand-main">
              ZIM<span className="text-[#C99A2E]">SMART</span>
            </span>
            <span className="admin-brand-sub">NEWSROOM CMS</span>
          </Link>

          {/* Close button inside mobile drawer */}
          <button
            type="button"
            className="admin-sidebar-close-btn"
            onClick={() => setMobileDrawerOpen(false)}
            aria-label="Close navigation drawer"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="admin-nav" aria-label="Newsroom navigation">
          {visibleSections.map((section) => (
            <div key={section.heading} className="admin-nav-group">
              <div className="admin-nav-heading">{section.heading}</div>
              <div className="admin-nav-items">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        `admin-nav-link ${isActive ? "active" : ""}`
                      }
                    >
                      <span className="admin-nav-pill-indicator" />
                      <Icon size={17} className="nav-icon" />
                      <span className="admin-nav-label">{item.label}</span>
                      {item.badge && (
                        <span className="admin-nav-badge">{item.badge}</span>
                      )}
                      <ChevronRight size={13} className="admin-nav-arrow" />
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <Link to="/" target="_blank" className="admin-view-site-btn">
            <span>View Public Magazine</span>
            <ExternalLink size={14} />
          </Link>
        </div>
      </aside>

      <div className="admin-main">
        {/* Top Control Bar */}
        <header className="admin-topbar">
          <div className="admin-topbar-left">
            {/* Mobile Drawer Trigger */}
            <button
              type="button"
              className="admin-mobile-toggle"
              onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}
              aria-label="Toggle navigation menu"
            >
              {mobileDrawerOpen ? <X size={20} /> : <Menu size={20} />}
            </button>

            <div className="admin-status-indicator">
              <span className="pulse-dot-emerald" />
              <span className="admin-status-text">Harare Newsroom • Live</span>
            </div>
          </div>

          <div className="admin-topbar-right">
            <Link to="/" target="_blank" className="admin-site-link">
              <Radio size={14} className="text-[#C99A2E]" />
              <span className="hidden sm:inline">Preview Live Site</span>
            </Link>

            <div className="admin-user-profile">
              <div className="admin-user-avatar">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className="admin-user-details hidden md:flex">
                <span className="admin-user-name">{user.name}</span>
                <span className="admin-user-role">
                  {user.roles.length ? user.roles[0] : "Editor"}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={logout}
              className="admin-logout-btn"
              title="Sign out of newsroom"
            >
              <LogOut size={15} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </header>

        <main className="admin-content-area">
          <Outlet />
        </main>

        <footer className="admin-footer">
          <span>ZimSmart Magazine Editorial CMS • Standard Edition</span>
          <span className="footer-dot">•</span>
          <span>Harare, Zimbabwe</span>
        </footer>
      </div>
    </div>
  );
}

